import { WeatherParameterForm } from "@/overlay/components/WeatherParameterForm";

import { getLanguageDescriptionField, indexWasMatched } from "@/overlay/components/WeatherParameterSearchForm/util";
import * as windowManager from "@/overlay/components/window";
import { classNames } from "@/utility/jsx";
import { searchEngineWeatherModels, useSearchEngineReady } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import type { CrossModelParameterSpecification } from "@mm/api-layers.meteomatics.com";
import Logger from "@mm/logging";
import {
  type ExactMatchResult,
  type HasLayerIdxNarrowing,
  type PartialWeatherParameter,
  computeTypeahead,
} from "@mm/weather-parameter-utils";
import { autofill } from "@mm/weather-parameter-utils";
import type { SearchResults } from "@mm/weather-parameter-utils";
import type { WeatherParamSearchParallel } from "@mm/weather-parameter-utils/search-engines/parallel";
import { useCallback, useEffect, useState } from "react";
import { ModelDropdownWithStation } from "../../../components/molecules/ModelDropdown/ModelDropdownWithStation";
import { SmartSelector } from "../CommonParts/SmartSelector";
import { FormField } from "../form-field";
import "./style.scss";

const logger = Logger.fromFilename(__filename);

function SearchResultList(props: {
  results: SearchResults;
  /**
   * A callback for when the user selected a parameter group model.
   * E.g) Temperture
   */
  onSelectLayerGroup: (result: CrossModelParameterSpecification) => void;
  /**
   * A callback for when the user selected the exact weather parameter.
   * E.g) t_2m:C
   */
  onSelectExactParamMatch: (result: ExactMatchResult) => void;
}) {
  const locale = useLingui().i18n.locale;
  const descriptionField = getLanguageDescriptionField(locale.substring(0, 2));

  if (props.results.fuzzy.length === 0 && props.results.regular.length === 0) {
    return (
      <span>
        <Trans>No parameter matches the given search term</Trans>
      </span>
    );
  }

  function onSelectLayerGroup(result: CrossModelParameterSpecification) {
    props.onSelectLayerGroup(result);
  }

  function onSelectExactParam(result: ExactMatchResult) {
    props.onSelectExactParamMatch(result);
  }

  // TODO: render weather models with their display name
  return (
    <div className="parameter-add__search-result listing listing--actionable box-section--breakout">
      {props.results.regular.map((res) => (
        <div
          className="search-result__item"
          key={`regular:${res.layerGroup.name}`}
          onClick={() => onSelectExactParam(res)}
        >
          <div className="search-result__item__title">{res.layerGroup[descriptionField] as string}</div>
          <div className="search-result__item__layers">
            {res.layerGroup.layers.map((layer, layerIdx) => (
              <span
                key={layer.format + layer.unit}
                className={classNames("layer", indexWasMatched(res.matchingLayers, layerIdx) && "layer--highlighted")}
              >
                {layer.format}
              </span>
            ))}
          </div>
          <div className="search-result__item__models">
            {res.layerGroup.available_models?.map((model) => (
              <span key={model}>{searchEngineWeatherModels.matchModelExactly(model)?.name ?? model}</span>
            ))}
          </div>
        </div>
      ))}
      {props.results.fuzzy.map((res) => (
        <div
          className="search-result__item"
          key={`fuzzy:${res.item.name}`}
          onClick={() => onSelectLayerGroup(res.item)}
        >
          <div className="search-result__item__title">{res.item[descriptionField] as string}</div>
          <div className="search-result__item__layers">
            {res.item.layers.map((layer, layerIdx) => (
              <span key={layer.format + layer.unit} className="layer">
                {layer.format}
              </span>
            ))}
          </div>
          <div className="search-result__item__models">
            {res.item.available_models?.map((model) => (
              <span key={model}>{searchEngineWeatherModels.matchModelExactly(model)?.name ?? model}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Input field that allows you to show faded out text in the background.
 *
 * Example usage: `<InputWithTypeahead value="green ba" typeahead="green bananas" />`
 */
export function InputWithTypeahead(
  props: { typeahead: string } & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
) {
  const { typeahead, ...forwarded } = props;
  return (
    <div className="InputWithTypeahead">
      <span className="InputWithTypeahead__typeahead">{typeahead}</span>
      <span className="InputWithTypeahead__icon">
        <span className="material-icons">search</span>
      </span>
      <input
        type="text"
        className="InputWithTypeahead__input"
        {...forwarded}
        placeholder={t`Search for weather parameters`}
      />
    </div>
  );
}

function WeatherParameterSearchInput(props: {
  /**
   * Make sure to always pass the same instance of search engine if the search engine is the same.
   * Otherwise, the life cycle hook will trigger the search term refreshing.
   */
  searchEngine: WeatherParamSearchParallel;
  onChange: (result: SearchResults) => void;
  /**
   * A callback for when the user typed in an exact string of weather parameter and hit enter.
   * E.g) t_2m:C
   */
  onSelectExactParamMatch: (result: ExactMatchResult) => void;
}) {
  const searchEngineReady = useSearchEngineReady();
  const [searchterm, setSearchterm] = useState("");
  const [searchResults, setSearchResults] = useState<null | SearchResults>(null);
  const [typeahead, setTypeahead] = useState("");
  const onChange = props.onChange;

  const updateSearchtermAndResults = useCallback(
    (newSearchterm: string) => {
      setSearchterm(newSearchterm);

      props.searchEngine
        .search(newSearchterm)
        .then((response) => {
          setSearchResults(response.results);
          if (response.results) {
            onChange(response.results);
          }
          // TODO: progress typeahead and invalidate if the prefix no longer matches instead
          // of only updating if a new search result arrives.
          let newTypeahead = "";

          if (response.results) {
            newTypeahead = computeTypeahead(response.results.regular) ?? "";
          }

          setTypeahead(newTypeahead);
        })
        .catch((e) => {
          // TODO: errors here are most likely reoccuring. We should probably show an error in this case instead of logging.
          // However, any error here renders the application unusable... So, adding CI tests to prevent this is most likely
          // a better option than a nice error message.
          logger.error(`search for term probably close to ${searchterm} errored out.`, e);
        });
    },
    [props.searchEngine, searchterm, onChange],
  );

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Enter" && searchResults && searchResults.regular[0]) {
      updateSearchtermAndResults("");
      // props.onChange(searchResults);
      props.onSelectExactParamMatch(searchResults.regular[0]);
    }
  }

  useEffect(() => {
    // Refresh the search results when the search engine changed.
    updateSearchtermAndResults(searchterm);
  }, [props.searchEngine, searchterm, updateSearchtermAndResults]);

  if (!searchEngineReady) {
    return <></>;
  }

  return (
    <div className="WeatherParameterSearch">
      <InputWithTypeahead
        autoFocus={true}
        value={searchterm}
        typeahead={typeahead}
        onKeyDown={(e) => handleKeyDown(e)}
        onChange={(e) => updateSearchtermAndResults(e.target.value)}
      />
    </div>
  );
}

type WeatherParameterSearchFormProps = {
  searchEngineOptions: { label: string; searchEngine: WeatherParamSearchParallel }[]; //We could directly pass search engines if mode is no longer releavant in the future.
  onSelectLayerGroup: (parameter: CrossModelParameterSpecification) => void;
  onSelectExactParam: (result: ExactMatchResult) => void;
  /**
   * A callback to notify the parent component when a search engine has been changed by a user.
   */
  onChangeSearchEngine?: () => void;
};
/**
 * A search form container that includes
 *  1) Selector that allows a user to switch different search engines based on the  mode.
 *  2) Text search for weather parameters.
 *  3) Show the results and provide a callback on selecting a parameter.
 */
export function WeatherParameterSearchForm(props: WeatherParameterSearchFormProps) {
  const searchEngineOptions = props.searchEngineOptions;

  const getFirstSearchEngineFromOption = (
    searchEngineOptions: { label: string; searchEngine: WeatherParamSearchParallel }[],
  ) => {
    return searchEngineOptions[0] !== undefined ? searchEngineOptions[0].searchEngine : null;
  };
  const [selectedSearchEngine, setSelectedSearchEngine] = useState(getFirstSearchEngineFromOption(searchEngineOptions));
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);

  useEffect(() => {
    // Change the search engine when swiching the search engine options.
    setSelectedSearchEngine(getFirstSearchEngineFromOption(searchEngineOptions));
    setSearchResults(null);
  }, [searchEngineOptions]);

  function onSelectLayerGroup(param: CrossModelParameterSpecification) {
    setSearchResults(null);
    props.onSelectLayerGroup(param);
  }

  function onSelectExactParam(result: ExactMatchResult) {
    setSearchResults(null);
    props.onSelectExactParam(result);
  }

  return (
    <>
      {selectedSearchEngine && (
        <div className={"box-section box-section--breakout"}>
          <div className={"weather-param-search-input-row "}>
            {searchEngineOptions.length > 1 && (
              <SmartSelector
                className={"search-engine-selector"}
                onSelect={(searchEngine) => {
                  if (searchEngine) {
                    setSelectedSearchEngine(searchEngine);
                    props.onChangeSearchEngine?.();
                  }
                }}
                items={searchEngineOptions.map((searchEngineOption, i) => {
                  return { value: searchEngineOption.searchEngine, label: searchEngineOption.label, key: i };
                })}
              />
            )}
            <WeatherParameterSearchInput
              searchEngine={selectedSearchEngine}
              onChange={setSearchResults}
              onSelectExactParamMatch={onSelectExactParam}
            />
          </div>
        </div>
      )}
      {selectedSearchEngine && searchResults && (
        <SearchResultList
          results={searchResults}
          onSelectLayerGroup={onSelectLayerGroup}
          onSelectExactParamMatch={onSelectExactParam}
        />
      )}
    </>
  );
}

export type WeatherParamPickerProps = {
  searchEngineOptions: { label: string; searchEngine: WeatherParamSearchParallel }[];
  onChange: (param: HasLayerIdxNarrowing<PartialWeatherParameter>) => void;
  onModelChange: (model: string | null) => void;
  isEdit: boolean;
  currentModel: string | null;
};
/**
 * A wrapper of weather parameter components that allows a user to search and select a weather parameter and its options
 * in different context.
 *
 * The component includes
 *    1) Parameter search form
 *    2) Parameter option
 *
 * @see {WeatherParameterSearchForm}
 * @see {WeatherParameterForm}
 */
export function WeatherParamPicker(props: WeatherParamPickerProps) {
  const [currentParam, setCurrentParam] = useState<PartialWeatherParameter | null>(null);

  function onSelectLayerGroup(result: CrossModelParameterSpecification) {
    const param = autofill({
      parameter: {
        ...result,
      },
    });
    setCurrentParam(param);
    props.onChange(param);
  }

  function onSelectExactParam(result: ExactMatchResult) {
    const param = autofill({
      parameter: result.layerGroup,
      narrowed_selection: {
        layerIdx: result.matchingLayers[0].layerIdx,
        // biome-ignore lint/style/noNonNullAssertion: TODO a lot of places
        fields: result.matchingLayers[0].match.groups!,
      },
    });
    setCurrentParam(param);
    props.onChange(param);
  }

  function onChangeParamOptions(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    setCurrentParam(param);
    props.onChange(param);
  }

  return (
    <>
      {
        <windowManager.WindowSection>
          <WeatherParameterSearchForm
            searchEngineOptions={props.searchEngineOptions}
            onSelectLayerGroup={onSelectLayerGroup}
            onSelectExactParam={onSelectExactParam}
            onChangeSearchEngine={() => setCurrentParam(null)}
          />
        </windowManager.WindowSection>
      }

      <windowManager.WindowSection>
        {currentParam && (
          <>
            <WeatherParameterForm
              showExactParameter={true}
              withTitle={true}
              autofill={false}
              parameter={currentParam}
              onChange={onChangeParamOptions}
            />
            {!props.isEdit && (
              <FormField label={t`Source`}>
                <ModelDropdownWithStation
                  onModelChange={props.onModelChange}
                  availableModels={currentParam.parameter.available_models}
                  currentModel={props.currentModel}
                  hasStationModels={true}
                />
              </FormField>
            )}
          </>
        )}
      </windowManager.WindowSection>
    </>
  );
}
